export interface APIReference {
  method: string
  category: string
  description: string
  parameters: string
  demoPage: string | null
  demoPagePath: string | null
  status: 'implemented' | 'partial' | 'not-implemented'
  notes?: string
  docsUrl?: string
}

export const apiData: APIReference[] = [
  // Core Widget Lifecycle
  {
    method: "zE('messenger', 'show')",
    category: 'Core',
    description: 'Displays the widget on the page',
    parameters: 'None',
    demoPage: 'Conversational Embed',
    demoPagePath: '/embed/conversational',
    status: 'implemented',
    docsUrl: 'https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/#show'
  },
  {
    method: "zE('messenger', 'hide')",
    category: 'Core',
    description: 'Hides all parts of the widget from the page',
    parameters: 'None',
    demoPage: 'Conversational Embed',
    demoPagePath: '/embed/conversational',
    status: 'implemented',
    docsUrl: 'https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/#hide'
  },
  {
    method: "zE('messenger', 'open')",
    category: 'Core',
    description: 'Opens the messaging Web Widget',
    parameters: 'None',
    demoPage: 'Multiple pages',
    demoPagePath: '/messaging/metadata',
    status: 'implemented',
    docsUrl: 'https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/#open'
  },
  {
    method: "zE('messenger', 'close')",
    category: 'Core',
    description: 'Closes the messaging Web Widget',
    parameters: 'None',
    demoPage: 'Conversational Embed',
    demoPagePath: '/embed/conversational',
    status: 'implemented',
    docsUrl: 'https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/#close'
  },

  // Settings
  {
    method: "zE('messenger:set', 'locale', newLocale)",
    category: 'Settings',
    description: 'Sets the locale of the messaging Web Widget',
    parameters: 'newLocale: string (e.g., "es", "fr", "ja")',
    demoPage: 'Layout Settings',
    demoPagePath: '/customization/layout',
    status: 'implemented',
    notes: 'Also demonstrated in French Proactive page',
    docsUrl: 'https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/#set-locale'
  },
  {
    method: "zE('messenger:set', 'zIndex', newZIndex)",
    category: 'Settings',
    description: 'Sets the CSS z-index on all widget iframes',
    parameters: 'newZIndex: number (default: 999999)',
    demoPage: 'Layout Settings',
    demoPagePath: '/customization/layout',
    status: 'implemented',
    docsUrl: 'https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/#set-zindex'
  },
  {
    method: "zE('messenger:set', 'cookies', range)",
    category: 'Settings',
    description: 'Controls which cookies the widget stores (GDPR compliance)',
    parameters: 'range: "all" | "functional" | "none"',
    demoPage: 'Cookie Consent',
    demoPagePath: '/messaging/cookies',
    status: 'implemented',
    docsUrl: 'https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/#set-cookies'
  },

  // Authentication
  {
    method: "zE('messenger', 'loginUser', callback)",
    category: 'Authentication',
    description: 'Authenticates user with JWT token',
    parameters: 'callback: function that receives token callback',
    demoPage: 'Authentication',
    demoPagePath: '/messaging/authentication',
    status: 'implemented',
    docsUrl: 'https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/#login-user'
  },
  {
    method: "zE('messenger', 'logoutUser')",
    category: 'Authentication',
    description: 'Logs out the authenticated user',
    parameters: 'None',
    demoPage: 'Authentication',
    demoPagePath: '/messaging/authentication',
    status: 'implemented',
    docsUrl: 'https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/#logout-user'
  },

  // Conversation Management
  {
    method: "zE('messenger:set', 'conversationFields', fields)",
    category: 'Conversation',
    description: 'Sets values for custom ticket fields (structured)',
    parameters: 'fields: Array<{id: string, value: string|number|boolean}>',
    demoPage: 'Metadata',
    demoPagePath: '/messaging/metadata',
    status: 'implemented',
    docsUrl: 'https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/#set-conversation-fields'
  },
  {
    method: "zE('messenger:set', 'conversationTags', tags)",
    category: 'Conversation',
    description: 'Adds tags to conversations for routing/organization',
    parameters: 'tags: string[] (array of tag strings)',
    demoPage: 'Contact-based Proactive',
    demoPagePath: '/proactive/contact',
    status: 'implemented',
    docsUrl: 'https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/#set-conversation-tags'
  },
  {
    method: "zE('messenger:set', 'conversationMetadata', metadata)",
    category: 'Conversation',
    description: 'Stores free-form key-value pairs for custom context',
    parameters: 'metadata: Record<string, string> (object with key-value pairs)',
    demoPage: 'Conversation Metadata',
    demoPagePath: '/messaging/conversation-metadata',
    status: 'implemented',
    docsUrl: 'https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/#set-conversation-metadata'
  },

  // UI Actions
  {
    method: "zE('messenger:ui', 'newConversation')",
    category: 'UI Actions',
    description: 'Starts a new conversation',
    parameters: 'Optional: {displayName, message: {content}}',
    demoPage: 'Home Screen Embed',
    demoPagePath: '/embed/homescreen',
    status: 'implemented',
    notes: 'Demonstrated with and without pre-filled message',
    docsUrl: 'https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/'
  },

  // Customization
  {
    method: "zE('messenger:set', 'customization', options)",
    category: 'Customization',
    description: 'Customizes widget appearance (headers, buttons, etc.)',
    parameters: 'options: object with customization settings',
    demoPage: 'Multiple embed pages',
    demoPagePath: '/embed/conversational',
    status: 'implemented',
    notes: 'Hide headers, hide new conversation button, etc.',
    docsUrl: 'https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/'
  },

  // Voice
  {
    method: "zE('messenger:open', 'voice', channelId)",
    category: 'Voice',
    description: 'Opens voice channel directly',
    parameters: 'channelId: string (voice channel ID)',
    demoPage: 'Voice',
    demoPagePath: '/messaging/voice',
    status: 'implemented',
    docsUrl: 'https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/'
  },

  // Events
  {
    method: "zE('messenger', 'on', 'unreadMessages', callback)",
    category: 'Events',
    description: 'Listens to unread message count changes',
    parameters: 'callback: (count: number) => void',
    demoPage: 'Custom Launcher',
    demoPagePath: '/customization/custom-launcher',
    status: 'implemented',
    docsUrl: 'https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/#unread-messages'
  },
  {
    method: "zE('messenger', 'on', 'open', callback)",
    category: 'Events',
    description: 'Fires when widget is opened',
    parameters: 'callback: () => void',
    demoPage: 'Custom Launcher',
    demoPagePath: '/customization/custom-launcher',
    status: 'implemented',
    docsUrl: 'https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/'
  },
  {
    method: "zE('messenger', 'on', 'close', callback)",
    category: 'Events',
    description: 'Fires when widget is closed',
    parameters: 'callback: () => void',
    demoPage: 'Custom Launcher',
    demoPagePath: '/customization/custom-launcher',
    status: 'implemented',
    docsUrl: 'https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/'
  },

  // Embedded Rendering
  {
    method: "zE('messenger', 'render', config)",
    category: 'Embed',
    description: 'Renders widget in embedded mode',
    parameters: 'config: {mode: "embedded", widget/conversationList/messageLog: {targetElement}}',
    demoPage: 'Multiple embed pages',
    demoPagePath: '/embed/contact',
    status: 'implemented',
    notes: 'Single widget, multi-column, and message-only modes demonstrated',
    docsUrl: 'https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/'
  }
]

export const categories = [
  'All',
  'Core',
  'Settings',
  'Authentication',
  'Conversation',
  'UI Actions',
  'Customization',
  'Voice',
  'Events',
  'Embed'
]
